import React from "react";
import PropTypes from "prop-types";
import { cmsTextClass, primaryButtonLargeClass, sheetClass } from "../../styles";

/** Up to two initials, for the logo tile of a business with no logo. */
const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

/**
 * Site1 hero: the business's mark, its headline, a line of introduction and the
 * booking button, centred.
 *
 * Two surfaces. Without a cover image it is a wash of the business's own colour
 * — the template ships no stock photography. With one, the photo fills the
 * section under a dark scrim and the copy turns white: dark text over a photo
 * nobody has seen yet is a gamble on what is behind each letter.
 */
const HeroSection = ({
  title,
  description = null,
  imageUrl = null,
  logo = null,
  businessName = null,
  buttonText = null,
  onButtonClick = () => {},
}) => {
  const onPhoto = Boolean(imageUrl);
  const initials = initialsOf(businessName);

  return (
    <section
      id="home"
      className={`relative isolate overflow-hidden ${
        onPhoto
          ? "bg-ink-900"
          : "bg-[linear-gradient(165deg,var(--site1-primary-muted)_0%,var(--site1-primary-soft)_55%,#ffffff_100%)]"
      }`}
    >
      {onPhoto ? (
        <>
          <img src={imageUrl} alt="" className="absolute inset-0 -z-10 size-full object-cover" />
          <div
            className="absolute inset-0 -z-10 bg-linear-to-b from-ink-900/55 via-ink-900/45 to-ink-900/65"
            aria-hidden="true"
          />
        </>
      ) : (
        // Soft shapes in the brand's tint, so a page with no images still has
        // depth. Decoration only; clipped by the section.
        <div aria-hidden="true">
          <div className="absolute -top-24 -left-24 -z-10 size-72 rounded-full bg-[var(--site1-primary-muted)] opacity-70 blur-3xl" />
          <div className="absolute -right-20 -bottom-28 -z-10 size-80 rounded-full bg-[var(--site1-primary-muted)] opacity-60 blur-3xl" />
        </div>
      )}

      <div className={`${sheetClass} flex flex-col items-center py-14 text-center md:py-24`}>
        {(logo || initials) && (
          <div className="flex size-18 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-ink-900/5 md:size-20">
            {logo ? (
              <img src={logo} alt="" className="size-full object-contain p-2" />
            ) : (
              <span
                className="text-2xl font-bold tracking-tight text-[var(--site1-primary-strong)] md:text-3xl"
                aria-hidden="true"
              >
                {initials}
              </span>
            )}
          </div>
        )}

        <h1
          className={`${cmsTextClass} mt-6 mb-0 max-w-3xl text-[clamp(1.875rem,1.35rem+2.4vw,3.25rem)] leading-tight font-bold tracking-tight ${
            onPhoto ? "text-white" : "text-[var(--site1-heading)]"
          }`}
        >
          {title}
        </h1>

        {description && (
          <p
            className={`${cmsTextClass} mt-4 mb-0 max-w-2xl text-fluid-lg leading-relaxed whitespace-pre-line ${
              onPhoto ? "text-white/85" : "text-ink-600"
            }`}
          >
            {description}
          </p>
        )}

        {buttonText && (
          <button
            type="button"
            onClick={onButtonClick}
            className={`${primaryButtonLargeClass} mt-8`}
          >
            {buttonText}
          </button>
        )}
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  logo: PropTypes.string,
  businessName: PropTypes.string,
  buttonText: PropTypes.string,
  onButtonClick: PropTypes.func,
};

export default HeroSection;
